import { Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconDots,
    IconFolderShare,
    IconPlayerPlay,
    IconPlugConnected,
    IconPower,
    IconReload,
    IconRefresh,
    IconTrash,
} from '@tabler/icons-react';
import { BaremetalInstance } from '@xver/@types/instance';
import { BaremetalCommand } from '@xver/@types/launch';
import { AccessBadge, Button, ConfirmActionModal } from '@xver/components/Atoms';
import { ConnectInstanceModal, ConnectTarget } from '@xver/components/Organisms';
import { useSendBaremetalCommandMutate } from '@xver/hooks/CloudComputeHooks/useSendBaremetalCommandMutate';
import { useActiveProjectScope } from '@xver/hooks/useActiveProject';
import { moveTriggerProps, useMoveResource } from '@xver/hooks/useMoveResource';
import { shortCpuLabel } from '@xver/utils/baremetal';
import { useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import BaremetalAvatar from '../../BareMetalComputePage/components/BaremetalListTable/components/BaremetalAvatar';
import { STATUS_BADGE } from '../../BareMetalComputePage/components/BaremetalListTable/BaremetalListTable';

type ConfirmableCommand = Exclude<BaremetalCommand, 'start'>;

interface BaremetalHeaderProps {
    baremetal: BaremetalInstance;
    osLabel: string;
    connectTarget: ConnectTarget;
    /** False with view-only access: power and destroy actions are not rendered. */
    canManage: boolean;
    isDestroying: boolean;
    onRefresh: () => void;
    onDestroy: () => void;
    /** Attached to the page `h1`; pass the ref from `usePageHeading`. */
    headingRef?: Ref<HTMLHeadingElement>;
}

export default function BaremetalHeader({
    baremetal,
    osLabel,
    connectTarget,
    canManage,
    isDestroying,
    onRefresh,
    onDestroy,
    headingRef,
}: BaremetalHeaderProps) {
    const { t } = useTranslation('bareMetal');
    const commandMutation = useSendBaremetalCommandMutate();
    const [confirmCommand, setConfirmCommand] = useState<ConfirmableCommand | null>(null);
    const [connectOpened, { open: openConnect, close: closeConnect }] = useDisclosure(false);
    const { activeProjectId } = useActiveProjectScope();
    const { canMove, requestMove, moveModal } = useMoveResource('baremetal', {
        triggerStaysAfterMove: true,
    });

    const badge = STATUS_BADGE[baremetal.status];
    const isRunning = baremetal.status === 'running';
    const isStopped = baremetal.status === 'stopped';
    const isBusy = commandMutation.isPending || isDestroying;
    const pendingCommand = commandMutation.isPending
        ? commandMutation.variables?.command
        : undefined;

    const send = (command: BaremetalCommand) =>
        commandMutation.mutate(
            { id: baremetal.id, command },
            { onSettled: () => setConfirmCommand(null) }
        );

    return (
        <div className="w-full h-auto flex flex-col xl:flex-row justify-between items-start xl:items-end gap-4 xl:gap-8">
            <div className="flex flex-row justify-start items-center gap-3 min-w-0">
                <BaremetalAvatar hostname={baremetal.hostname} status={baremetal.status} />
                <div className="flex flex-col justify-start items-start min-w-0">
                    <div className="flex flex-row flex-wrap items-center gap-3">
                        <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold break-all">
                            {baremetal.hostname}
                        </h1>
                        {!canManage && <AccessBadge level="view" />}
                    </div>
                    <div className="text-sm flex flex-row flex-wrap justify-start items-center gap-x-2 text-fg/68">
                        <span
                            role="status"
                            className="px-1.5 py-0.5 text-xs font-medium rounded"
                            style={{ backgroundColor: badge?.bg, color: badge?.fg }}
                        >
                            {t(`status:${baremetal.status}`)}
                        </span>
                        {baremetal.network.ip_address && (
                            <>
                                <span className="text-3xl leading-none" aria-hidden>
                                    ·
                                </span>
                                <span className="font-mono">{baremetal.network.ip_address}</span>
                            </>
                        )}
                        <span className="text-3xl leading-none" aria-hidden>
                            ·
                        </span>
                        <span>{osLabel}</span>
                        <span className="text-3xl leading-none" aria-hidden>
                            ·
                        </span>
                        <span>{shortCpuLabel(baremetal.specs.cpu)}</span>
                    </div>
                </div>
            </div>

            <div
                role="group"
                aria-label={t('detail.power.label')}
                className="flex flex-row flex-wrap justify-start xl:justify-end items-center gap-2"
            >
                {canManage && isStopped && (
                    <Button
                        leftIcon={<IconPlayerPlay size={16} />}
                        isLoading={pendingCommand === 'start'}
                        disabled={isBusy}
                        onClick={() => send('start')}
                    >
                        {t('detail.power.start')}
                    </Button>
                )}
                {canManage && isRunning && (
                    <>
                        <Button
                            variant="secondary"
                            leftIcon={<IconRefresh size={16} />}
                            disabled={isBusy}
                            onClick={() => setConfirmCommand('reboot')}
                        >
                            {t('detail.power.reboot')}
                        </Button>
                        <Button
                            variant="secondary"
                            leftIcon={<IconPower size={16} />}
                            disabled={isBusy}
                            onClick={() => setConfirmCommand('shutdown')}
                        >
                            {t('detail.power.shutdown')}
                        </Button>
                    </>
                )}
                <Button
                    leftIcon={<IconPlugConnected size={16} />}
                    onClick={openConnect}
                    disabled={!isRunning}
                    className="whitespace-nowrap"
                >
                    {t('detail.connect')}
                </Button>
                <Menu withArrow position="bottom-end">
                    <Menu.Target>
                        <Button
                            {...moveTriggerProps('baremetal', baremetal.id)}
                            variant="secondary"
                            aria-label={t('detail.menu.label')}
                            title={t('detail.menu.label')}
                        >
                            <IconDots size={20} aria-hidden />
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<IconReload size={18} stroke={1.5} />}
                            onClick={onRefresh}
                        >
                            {t('detail.menu.refresh')}
                        </Menu.Item>
                        {canManage && (
                            <>
                                {canMove && baremetal.project_id === activeProjectId && (
                                    <Menu.Item
                                        leftSection={<IconFolderShare size={18} stroke={1.5} />}
                                        onClick={() =>
                                            requestMove({
                                                id: String(baremetal.id),
                                                name: baremetal.hostname,
                                            })
                                        }
                                    >
                                        {t('projects:resource.moveToProject')}
                                    </Menu.Item>
                                )}
                                <Menu.Divider />
                                <Menu.Item
                                    color="var(--color-status-danger)"
                                    leftSection={<IconTrash size={18} stroke={1.5} />}
                                    onClick={onDestroy}
                                    disabled={isDestroying}
                                >
                                    {t('detail.menu.destroy')}
                                </Menu.Item>
                            </>
                        )}
                    </Menu.Dropdown>
                </Menu>
            </div>

            <ConnectInstanceModal
                target={connectTarget}
                opened={connectOpened}
                onClose={closeConnect}
            />

            <ConfirmActionModal
                opened={confirmCommand !== null}
                onClose={() => setConfirmCommand(null)}
                title={
                    confirmCommand === 'reboot'
                        ? t('detail.power.rebootTitle')
                        : t('detail.power.shutdownTitle')
                }
                message={
                    confirmCommand === 'reboot'
                        ? t('detail.power.rebootMessage', { name: baremetal.hostname })
                        : t('detail.power.shutdownMessage', { name: baremetal.hostname })
                }
                confirmLabel={
                    confirmCommand === 'reboot'
                        ? t('detail.power.reboot')
                        : t('detail.power.shutdown')
                }
                confirmVariant="danger"
                isLoading={commandMutation.isPending}
                onConfirm={() => confirmCommand && send(confirmCommand)}
            />

            {moveModal}
        </div>
    );
}
